import { useEffect, useRef, useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { getLocationSuggestions } from '@/services/api';

const digits = (s) => String(s ?? '').replace(/\D/g, '');
const groupThousands = (s) => { const d = digits(s); return d ? Number(d).toLocaleString('en-US') : ''; };

function ToggleGroup({ options, value, onToggle, testid }) {
  const selected = Array.isArray(value) ? value : [];
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((opt) => {
        const active = selected.includes(String(opt.value));
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onToggle(String(opt.value))}
            className={cn(
              'rounded-xl border px-3 py-2 text-sm font-medium transition-colors',
              active ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-600 hover:border-blue-300'
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function RangeField({ def, filters, onChange }) {
  const [lo, hi] = def.keys;
  const fmt = def.thousands ? groupThousands : (v) => (v ?? '');
  const handle = (key) => (e) => {
    const raw = e.target.value;
    const next = def.thousands ? digits(raw) : raw.replace(/[^\d]/g, '');
    onChange({ [key]: next });
  };
  return (
    <div className="flex items-center gap-2">
      <Input
        type="text" inputMode="numeric" placeholder={def.placeholders?.[0] || 'Min'}
        value={fmt(filters[lo])} onChange={handle(lo)} className="rounded-xl" data-testid={`filter-${def.id}-min`}
      />
      <span className="text-slate-400">–</span>
      <Input
        type="text" inputMode="numeric" placeholder={def.placeholders?.[1] || 'Max'}
        value={fmt(filters[hi])} onChange={handle(hi)} className="rounded-xl" data-testid={`filter-${def.id}-max`}
      />
    </div>
  );
}

function BooleanField({ def, filters, onChange }) {
  const cur = String(filters[def.key] ?? '');
  const opts = [{ label: 'Any', value: '' }, { label: 'Yes', value: 'true' }, { label: 'No', value: 'false' }];
  return (
    <div className="flex flex-wrap gap-2" data-testid={`filter-${def.id}`}>
      {opts.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange({ [def.key]: o.value })}
          className={cn(
            'rounded-xl border px-3 py-2 text-sm font-medium transition-colors',
            cur === o.value ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-600 hover:border-blue-300'
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function SelectField({ def, filters, onChange }) {
  return (
    <select
      value={filters[def.key] || ''}
      onChange={(e) => onChange({ [def.key]: e.target.value })}
      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
      data-testid={`filter-${def.id}`}
    >
      {def.options.map((o) => <option key={String(o.value)} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function AutocompleteField({ def, filters, onChange, zip }) {
  const [q, setQ] = useState('');
  const [sugs, setSugs] = useState([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const current = filters[def.key] || '';

  useEffect(() => {
    if (!q || q.length < 2) { setSugs([]); return; }
    let alive = true;
    const t = setTimeout(() => {
      getLocationSuggestions(q).then((res) => {
        if (!alive) return;
        const list = (res || [])
          .filter((s) => (zip ? s.type === 'zip' : s.type !== 'zip'))
          .slice(0, 8);
        setSugs(list);
        setOpen(list.length > 0);
      });
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [q, zip]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const commit = (val) => { onChange({ [def.key]: val }); setQ(''); setSugs([]); setOpen(false); };
  const labelOf = (s) => s.label || [s.city, s.state].filter(Boolean).join(', ') || s.zip || s.value || '';

  return (
    <div ref={boxRef} className="relative">
      {current && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700">
            {current}
            <button type="button" onClick={() => onChange({ [def.key]: '' })} className="text-indigo-400 hover:text-indigo-700">×</button>
          </span>
        </div>
      )}
      <Input
        type="text"
        placeholder={zip ? 'e.g. 33426' : 'e.g. Boca Raton, Miami…'}
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          if (zip) onChange({ [def.key]: e.target.value.trim() });
        }}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit((q || '').trim()); } }}
        className="rounded-xl"
        data-testid={`filter-${def.id}`}
      />
      {open && (
        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
          {sugs.map((s, i) => (
            <button
              key={i}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); commit(zip ? (s.zip || labelOf(s)) : labelOf(s)); }}
              className="block w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
            >
              {labelOf(s)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function FilterField({ def, filters, onChange, hideLabel }) {
  let control = null;
  const toggle = (val) => {
    const cur = Array.isArray(filters[def.key]) ? filters[def.key] : [];
    const next = cur.includes(val) ? cur.filter((v) => v !== val) : [...cur, val];
    onChange({ [def.key]: next });
  };

  switch (def.type) {
    case 'multistatus':
    case 'checkbox':
    case 'multicheck':
      control = <ToggleGroup testid={`filter-${def.id}`} options={def.options.map((o) => (typeof o === 'object' ? o : { value: o, label: o }))} value={filters[def.key]} onToggle={toggle} />;
      break;
    case 'range':
      control = <RangeField def={def} filters={filters} onChange={onChange} />;
      break;
    case 'select':
      control = <SelectField def={def} filters={filters} onChange={onChange} />;
      break;
    case 'boolean':
      control = <BooleanField def={def} filters={filters} onChange={onChange} />;
      break;
    case 'text':
      control = <Input type="text" placeholder={def.placeholder || def.label} value={filters[def.key] || ''} onChange={(e) => onChange({ [def.key]: e.target.value })} className="rounded-xl" data-testid={`filter-${def.id}`} />;
      break;
    case 'location_autocomplete':
      control = <AutocompleteField def={def} filters={filters} onChange={onChange} />;
      break;
    case 'zip_autocomplete':
      control = <AutocompleteField def={def} filters={filters} onChange={onChange} zip />;
      break;
    default:
      control = null;
  }

  if (!control) return null;
  if (hideLabel) return control;
  return (
    <div>
      <Label className="text-sm font-semibold text-slate-900">{def.label}</Label>
      <div className="mt-2">{control}</div>
    </div>
  );
}
